import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureGet } from '../../config/api';
import { formatDate, formatDateTime } from '../../utils/format';
import { PrimaryButton } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SubscriptionForm from './SubscriptionForm';
import CancelSubscriptionPopup from './CancelSubscriptionPopup';
import Breadcrumbs from '../../components/Breadcrumbs';

import EditIcon from '../../icons/editB.svg';
import addIcon from '../../icons/plusB.svg';
import cancelIcon from '../../icons/x-circleB.svg';

const ClientDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('infosGenerales');
  const [loading, setLoading] = useState(false);
  const [subscriptionFormOpen, setSubscriptionFormOpen] = useState(false);
  const [cancelSubscriptionId, setCancelSubscriptionId] = useState(null);
  const { t } = useTranslation();
  const { id } = useParams();
  const [client, setClient] = useState({
    id: null,
    companyName: '',
    contactEmail: '',
    contactFirstName: '',
    contactLastName: '',
    expiry: null,
    trialExpiryDate: null,
    preferredLanguage: '',
    phoneNumber: '',
    grayClientStatus: '',
    grayClientId: '',
    subscription: null,
    hosting: null,
  });
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();
    const showSnackbar = () => {};
    const fetchClient = async () => {
      setLoading(true);
      try {
        const response = await secureGet(`/grayClients/${id}`, showSnackbar, {
          signal: abortController.signal
        });
        setClient(response.data);
        console.log(response.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Failed to fetch client:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClient();

    return () => {
      abortController.abort();
    };
  }, [id]);

  useEffect(() => {
    const grayClientId = client?.id ?? client?.grayClientId;
    if (!grayClientId) return;
    const abortController = new AbortController();
    const showSnackbar = () => {};
    const fetchSubscription = async () => {
      try {
        const response = await secureGet(
          `/subscriptions/by-grayClient/${grayClientId}`,
          showSnackbar,
          { signal: abortController.signal }
        );
        console.log(response.data);
        if (response?.data != null) setSubscriptionData(response.data);
      } catch (e) {
        if (e.name !== 'AbortError') setSubscriptionData(null);
      }
    };
    fetchSubscription();
    return () => abortController.abort();
  }, [client?.id, client?.grayClientId]);

  const displayName = client.companyName || client.legalName || client.name || '';

  const handleSubscriptionFormSuccess = async () => {
    setSubscriptionFormOpen(false);
    await refetchSubscriptionAndGrayClient();
  };

  const refetchSubscriptionAndGrayClient = async () => {
    const showSnackbar = () => {};
    const grayClientRes = await secureGet(`/grayClients/${id}`, showSnackbar, {}).catch(() => null);
    if (grayClientRes?.data) setClient(grayClientRes.data);
    const grayClientId = grayClientRes?.data?.id ?? grayClientRes?.data?.grayClientId ?? client?.id ?? client?.grayClientId ?? id;
    if (grayClientId) {
      const subRes = await secureGet(`/subscriptions/by-grayClient/${grayClientId}`, showSnackbar, {}).catch(() => null);
      if (subRes?.data != null) setSubscriptionData(subRes.data);
    }
  };

  const handleModifyClick = () => {
    history.push({
      pathname: `/clients/details/modify/${client.id}`,
      state: { initialValues: client, filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
    });
  };
  if (!loading && !client?.id && (client?.companyName ?? '') === '') {
    return <p>{t('clientDetails.notFound') || 'Client not found'}</p>;
  }

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.client_details")}</title>
      </Helmet>
      <div className="flex flex-col mb-[20px]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Breadcrumbs />
            <h1 className="text-[32px] font-semibold">{t(`titles.client_details`)}</h1>
          </div>
          <div className="ml-auto text-[14px]">
            <PrimaryButton label={t(`primaryButtons.modify`)} icon={EditIcon} onClick={handleModifyClick} />
          </div>
        </div>

        <div className="flex items-center space-x-2 ">
          <p className="text-[24px] font-semibold text-primary">
            {displayName}
          </p>
        </div>
      </div>

      <div className="flex bg-gray-200 rounded-[12px] p-1 w-fit mb-2">
        <button
          className={`px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === 'infosGenerales'
            ? 'bg-white text-[#043F88] shadow'
            : 'text-[#ACAFC6]'
            }`}
          onClick={() => setActiveTab('infosGenerales')}
        >
          {t(`titles.general_infos`).toUpperCase()}
        </button>
        <button
          className={`px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === 'subscription'
            ? 'bg-white text-[#043F88] shadow'
            : 'text-[#ACAFC6]'
            }`}
          onClick={() => setActiveTab('subscription')}
        >
          {t(`fields.clients.subscriptionTitle`).toUpperCase()}
        </button>
      </div>

      {activeTab === "infosGenerales" && (
        <div className="flex gap-3 mt-6">
          <div className="w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Input label={t(`signup_fields.companyName`)} value={client.companyName ?? ''} onChange={() => { }} disabled={true} />
              <Input label={t(`columns.grayClientId`)} value={client.grayClientId != null ? String(client.grayClientId) : ''} onChange={() => { }} disabled={true} />
              <Input label={t(`signup_fields.contactFirstName`)} value={client.contactFirstName ?? ''} onChange={() => { }} disabled={true} />
              <Input label={t(`signup_fields.contactLastName`)} value={client.contactLastName ?? ''} onChange={() => { }} disabled={true} />
              <Input label={t(`signup_fields.contactEmail`)} value={client.contactEmail ?? ''} onChange={() => { }} disabled={true} />
              <Input label={t(`signup_fields.companyPhone`)} value={client.phoneNumber ?? ''} onChange={() => { }} disabled={true} />
              <Input label={t(`signup_fields.preferredLanguage`)} value={client.preferredLanguage ? (t(`lang.${client.preferredLanguage}`) || client.preferredLanguage) : ''} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.storageBucket`)} value={client.bucketName ?? ''} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.trialExpiryDate`)} value={client.trialExpiryDate ? formatDate(client.trialExpiryDate) : ''} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.grayClientStatusLabel`)} value={client.grayClientStatus ? (t(`clientStatus.${client.grayClientStatus}`) || client.grayClientStatus) : ''} onChange={() => { }} disabled={true} />
              <Input label={t(`clientDetails.expiry`)} value={client.expiry ? formatDate(client.expiry) : ''} onChange={() => { }} disabled={true} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'subscription' && (() => {
        const sub = subscriptionData?.subscription != null
          ? (Array.isArray(subscriptionData.subscription) ? subscriptionData.subscription[0] : subscriptionData.subscription)
          : (Array.isArray(subscriptionData) ? subscriptionData[0] : subscriptionData);
        const hosting = subscriptionData?.hosting ?? client.hosting ?? {};
        return (
          <div className="">
            <div className="flex justify-end gap-2">
            {client?.grayClientStatus?.toLowerCase() !== 'cancelled' && (

              <PrimaryButton
                icon={addIcon}
                label={t('clientDetails.addModifySubscription')}
                onClick={() => setSubscriptionFormOpen(true)}
              />
              )}
              {sub?.id != null && client?.grayClientStatus?.toLowerCase() !== 'cancelled' && (
                <PrimaryButton
                  icon={cancelIcon}
                  color="#EF476F"
                  label={t('clientDetails.cancelSubscription')}
                  onClick={() => setCancelSubscriptionId(sub.id)}
                />
              )}
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-2">{t(`fields.clients.subscriptionTitle`)}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <Input label={t(`fields.clients.subscription.planName`)} value={sub?.subscriptionPlanName ? (t(`plans.${sub.subscriptionPlanName}`) || sub.subscriptionPlanName) : ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.billingIntervalLabel`)} value={sub?.subscriptionBillingInterval ? (t(`billingIntervals.${sub.subscriptionBillingInterval}`) || sub.subscriptionBillingInterval) : ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.discount`)} value={sub?.subscriptionDiscount ?? ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.currency`)} value={sub?.subscriptionCurrency ?? ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.tva`)} value={sub?.subscriptionTVA ?? ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.subscriptionStartDate`)} value={sub?.subscriptionStartDate ? formatDate(sub.subscriptionStartDate) : ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.currentPeriodStart`)} value={sub?.subscriptionCurrentPeriodStart ? formatDate(sub.subscriptionCurrentPeriodStart) : ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.currentPeriodEnd`)} value={sub?.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.nextBillingAt`)} value={sub?.nextBillingAt ? formatDate(sub.nextBillingAt) : ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.includedSeats`)} value={sub?.subscriptionIncludedSeats ?? ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.subscription.pricePerUser`)} value={sub?.subscriptionPricePerUser ?? ''} onChange={() => { }} disabled={true} />
                  </div>
              </div>
              <div>
                <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-2">{t(`fields.clients.hostingTitle`)}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  <Input label={t(`fields.clients.hosting.currentHostingMode`)} value={sub?.hostingMode ?? ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.hosting.environmentId`)} value={sub?.hostingEnvironmentId ?? ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.hosting.dbName`)} value={sub?.hostingDbName ?? ''} onChange={() => { }} disabled={true} />
                  <Input label={t(`fields.clients.hosting.baseDomain`)} value={sub?.hostingBaseDomain ?? ''} onChange={() => { }} disabled={true} />
                </div>
              </div>
              {(client?.grayClientStatus?.toLowerCase() === 'cancelled') && (
                <div>
                  <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-2">{t('clientDetails.cancellationTitle') || 'Cancellation'}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <Input label={t(`fields.clients.subscription.canceledAt`)} value={(sub?.cancelledAt ?? sub?.canceledAt) ? formatDateTime(sub.cancelledAt ?? sub.canceledAt) : ''} onChange={() => { }} disabled={true} />
                    <Input label={t(`fields.clients.subscription.cancelReason`)} value={sub?.cancellationReason ?? ''} onChange={() => { }} disabled={true} />
                  </div>
                  <div className="mt-4">
                    <Input label={t(`fields.clients.subscription.cancelNotes`)} value={sub?.cancellationNotes ?? ''} onChange={() => { }} disabled={true} />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <SubscriptionForm
        open={subscriptionFormOpen}
        onClose={() => setSubscriptionFormOpen(false)}
        client={client}
        subscriptionData={subscriptionData}
        onSuccess={handleSubscriptionFormSuccess}
      />

      <CancelSubscriptionPopup
        open={cancelSubscriptionId != null}
        onClose={() => setCancelSubscriptionId(null)}
        grayClientId={id}
        onSuccess={refetchSubscriptionAndGrayClient}
      />
    </div>
  );
};

export default ClientDetails;